import React from "react";
import { SkRect, SkEllipse, SkCircle, SkLine, SkPolygon } from "../prim";

/**
 * Anivaze v3 — CHARACTER actors (skin-aware, kid-relatable).
 *
 * Seed range: 1600–1799. All actors are pure functions of t; animations use
 * <g transform> so hand-drawn seeds stay stable. Composed ONLY from Sk*
 * primitives — skins are inherited automatically from context.
 */

// ─── shared palette ────────────────────────────────────────────────────────
const SKIN  = "#fcd34d";
const HAIR  = "#92400e";
const SHIRT_CHILD    = "#60a5fa";
const SHIRT_TEACHER  = "#6ee7b7";
const SHIRT_SCIENTIST= "#a78bfa";
const SHIRT_ROBOT    = "#94a3b8";
const SHIRT_WALKER   = "#fb923c";
const SHIRT_THINKER  = "#f472b6";
const PANTS = "#1e40af";
const FLASK_BODY = "#c4b5fd";
const FLASK_LIQ  = "#7c3aed";

// ─── skChild ──────────────────────────────────────────────────────────────
/**
 * A small child (shorter proportions) with a gentle idle bob and an optional
 * waving right arm.
 */
export function skChild(
  x: number,
  y: number,
  t: number,
  scale = 1,
  wave = false,
): React.ReactNode {
  const bob      = Math.sin(t * 2.2) * 2;
  const armWave  = wave ? Math.sin(t * 5) * 30 : 0;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform={`translate(0 ${bob})`}>
        {/* head */}
        <SkCircle cx={0} cy={-44} r={9} fill={SKIN} seed={1600} />
        {/* hair */}
        <SkEllipse cx={0} cy={-51} rx={9} ry={5} fill={HAIR} seed={1601} />
        {/* body */}
        <SkRect x={-8} y={-35} w={16} h={22} rx={3} fill={SHIRT_CHILD} seed={1602} />
        {/* left arm */}
        <SkLine x1={-8} y1={-33} x2={-14} y2={-18} seed={1603} />
        {/* right arm (waves) */}
        <g transform={`rotate(${armWave} -0 -33)`}>
          <SkLine x1={8} y1={-33} x2={14} y2={wave ? -46 : -18} seed={1604} />
        </g>
        {/* legs */}
        <SkLine x1={-4} y1={-13} x2={-5} y2={0} seed={1605} />
        <SkLine x1={4} y1={-13} x2={5} y2={0} seed={1606} />
      </g>
    </g>
  );
}

// ─── skTeacher ────────────────────────────────────────────────────────────
/**
 * A teacher facing a board with right arm pointing upward / forward.
 */
export function skTeacher(
  x: number,
  y: number,
  t: number,
  scale = 1,
): React.ReactNode {
  const pointBob = Math.sin(t * 1.8) * 3;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* chalkboard (backdrop, behind figure) */}
      <SkRect x={20} y={-80} w={60} h={50} rx={3} fill="#064e3b" seed={1610} />
      <SkLine x1={24} y1={-40} x2={76} y2={-40} stroke="#6ee7b7" strokeWidth={2} seed={1611} />
      {/* person */}
      {/* head */}
      <SkCircle cx={0} cy={-52} r={10} fill={SKIN} seed={1612} />
      <SkEllipse cx={0} cy={-60} rx={10} ry={5} fill={HAIR} seed={1613} />
      {/* body */}
      <SkRect x={-11} y={-42} w={22} h={28} rx={3} fill={SHIRT_TEACHER} seed={1614} />
      {/* left arm resting */}
      <SkLine x1={-11} y1={-38} x2={-16} y2={-22} seed={1615} />
      {/* right arm pointing at board */}
      <g transform={`translate(0 ${pointBob})`}>
        <SkLine x1={11} y1={-38} x2={28} y2={-58} seed={1616} />
      </g>
      {/* legs */}
      <SkLine x1={-5} y1={-14} x2={-6} y2={0} seed={1617} />
      <SkLine x1={5} y1={-14} x2={6} y2={0} seed={1618} />
    </g>
  );
}

// ─── skScientist ──────────────────────────────────────────────────────────
/**
 * A scientist with goggles, holding a flask whose liquid sloshes with t.
 */
export function skScientist(
  x: number,
  y: number,
  t: number,
  scale = 1,
): React.ReactNode {
  // flask tilt oscillates like a gentle swirl
  const flaskTilt  = Math.sin(t * 2.5) * 8;
  // liquid fill height bobs
  const liqLevel   = 5 + Math.sin(t * 3) * 3;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* head */}
      <SkCircle cx={0} cy={-52} r={10} fill={SKIN} seed={1620} />
      {/* hair */}
      <SkEllipse cx={0} cy={-60} rx={10} ry={5} fill={HAIR} seed={1621} />
      {/* goggles */}
      <SkEllipse cx={-5} cy={-53} rx={5} ry={4} fill="#bae6fd" seed={1622} />
      <SkEllipse cx={5} cy={-53} rx={5} ry={4} fill="#bae6fd" seed={1623} />
      <SkLine x1={0} y1={-53} x2={0} y2={-53} seed={1624} />
      {/* body / lab coat */}
      <SkRect x={-11} y={-42} w={22} h={28} rx={3} fill="#f1f5f9" seed={1625} />
      {/* left arm */}
      <SkLine x1={-11} y1={-38} x2={-16} y2={-22} seed={1626} />
      {/* right arm holding flask */}
      <SkLine x1={11} y1={-38} x2={18} y2={-24} seed={1627} />
      {/* flask (tilts) */}
      <g transform={`translate(20 -18) rotate(${flaskTilt})`}>
        {/* flask body */}
        <SkEllipse cx={0} cy={0} rx={7} ry={9} fill={FLASK_BODY} seed={1628} />
        {/* flask neck */}
        <SkRect x={-3} y={-16} w={6} h={8} rx={1} fill={FLASK_BODY} seed={1629} />
        {/* liquid inside */}
        <SkEllipse cx={0} cy={liqLevel - 9} rx={6} ry={3} fill={FLASK_LIQ} seed={1630} />
      </g>
      {/* legs */}
      <SkLine x1={-5} y1={-14} x2={-6} y2={0} seed={1631} />
      <SkLine x1={5} y1={-14} x2={6} y2={0} seed={1632} />
    </g>
  );
}

// ─── skRobot ──────────────────────────────────────────────────────────────
/**
 * A cute boxy robot with an antenna and a blinking eye.
 */
export function skRobot(
  x: number,
  y: number,
  t: number,
  scale = 1,
): React.ReactNode {
  // blink: eye is nearly closed (very short ry) at brief intervals
  const blinkCycle = (t * 1.5) % (Math.PI * 2);
  const eyeRy      = blinkCycle > Math.PI * 1.8 ? 1 : 5;
  // antenna light bob
  const antBob     = Math.sin(t * 3) * 3;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* antenna */}
      <SkLine x1={0} y1={-72} x2={0} y2={-60} seed={1640} />
      <g transform={`translate(0 ${antBob})`}>
        <SkCircle cx={0} cy={-76} r={4} fill="#fbbf24" seed={1641} />
      </g>
      {/* head (box) */}
      <SkRect x={-14} y={-60} w={28} h={22} rx={4} fill={SHIRT_ROBOT} seed={1642} />
      {/* left eye */}
      <SkEllipse cx={-6} cy={-50} rx={4} ry={eyeRy} fill="#0ea5e9" seed={1643} />
      {/* right eye */}
      <SkEllipse cx={6} cy={-50} rx={4} ry={eyeRy} fill="#0ea5e9" seed={1644} />
      {/* mouth / speaker grille */}
      <SkRect x={-8} y={-42} w={16} h={4} rx={2} fill="#475569" seed={1645} />
      {/* body (box) */}
      <SkRect x={-13} y={-38} w={26} h={24} rx={3} fill="#cbd5e1" seed={1646} />
      {/* chest light */}
      <SkCircle cx={0} cy={-26} r={4} fill="#ef4444" seed={1647} />
      {/* arms */}
      <SkRect x={-22} y={-36} w={8} h={18} rx={3} fill={SHIRT_ROBOT} seed={1648} />
      <SkRect x={14} y={-36} w={8} h={18} rx={3} fill={SHIRT_ROBOT} seed={1649} />
      {/* legs */}
      <SkRect x={-12} y={-14} w={9} h={14} rx={2} fill={SHIRT_ROBOT} seed={1650} />
      <SkRect x={3} y={-14} w={9} h={14} rx={2} fill={SHIRT_ROBOT} seed={1651} />
    </g>
  );
}

// ─── skWalker ─────────────────────────────────────────────────────────────
/**
 * A person caught mid-walk; legs alternate from t for journeys/travel scenes.
 */
export function skWalker(
  x: number,
  y: number,
  t: number,
  scale = 1,
): React.ReactNode {
  const legSwing  = Math.sin(t * 4) * 20;
  const armSwing  = Math.sin(t * 4 + Math.PI) * 20; // arms counter-swing
  const bodyBob   = Math.abs(Math.sin(t * 4)) * 2;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform={`translate(0 ${-bodyBob})`}>
        {/* head */}
        <SkCircle cx={0} cy={-52} r={10} fill={SKIN} seed={1660} />
        <SkEllipse cx={0} cy={-60} rx={10} ry={5} fill={HAIR} seed={1661} />
        {/* body */}
        <SkRect x={-11} y={-42} w={22} h={28} rx={3} fill={SHIRT_WALKER} seed={1662} />
        {/* left arm swings */}
        <g transform={`rotate(${armSwing} 0 -38)`}>
          <SkLine x1={-11} y1={-38} x2={-16} y2={-22} seed={1663} />
        </g>
        {/* right arm counter-swings */}
        <g transform={`rotate(${-armSwing} 0 -38)`}>
          <SkLine x1={11} y1={-38} x2={16} y2={-22} seed={1664} />
        </g>
        {/* left leg */}
        <g transform={`rotate(${legSwing} 0 -14)`}>
          <SkLine x1={-5} y1={-14} x2={-6} y2={4} seed={1665} />
        </g>
        {/* right leg */}
        <g transform={`rotate(${-legSwing} 0 -14)`}>
          <SkLine x1={5} y1={-14} x2={6} y2={4} seed={1666} />
        </g>
      </g>
    </g>
  );
}

// ─── skThinker ────────────────────────────────────────────────────────────
/**
 * A person with hand to chin, thinking; a "…" thought trail bobs overhead.
 */
export function skThinker(
  x: number,
  y: number,
  t: number,
  scale = 1,
): React.ReactNode {
  const dotBob   = Math.sin(t * 2) * 3;
  // small tilt of the head while thinking
  const headTilt = Math.sin(t * 1.2) * 5;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* thought bubbles — three dots that float */}
      <g transform={`translate(16 ${-66 + dotBob})`}>
        <SkCircle cx={0}  cy={0} r={3} fill="#e0e7ff" seed={1670} />
        <SkCircle cx={9}  cy={-5} r={4} fill="#e0e7ff" seed={1671} />
        <SkCircle cx={20} cy={-11} r={5} fill="#e0e7ff" seed={1672} />
      </g>
      {/* head (slight tilt) */}
      <g transform={`rotate(${headTilt} 0 -52)`}>
        <SkCircle cx={0} cy={-52} r={10} fill={SKIN} seed={1673} />
        <SkEllipse cx={0} cy={-60} rx={10} ry={5} fill={HAIR} seed={1674} />
      </g>
      {/* body */}
      <SkRect x={-11} y={-42} w={22} h={28} rx={3} fill={SHIRT_THINKER} seed={1675} />
      {/* left arm resting at side */}
      <SkLine x1={-11} y1={-38} x2={-15} y2={-22} seed={1676} />
      {/* right arm: hand to chin */}
      <SkLine x1={11} y1={-38} x2={8} y2={-52} seed={1677} />
      {/* chin "hand" dot */}
      <SkCircle cx={8} cy={-52} r={3} fill={SKIN} seed={1678} />
      {/* pants */}
      <SkRect x={-11} y={-14} w={10} h={14} rx={2} fill={PANTS} seed={1679} />
      <SkRect x={1} y={-14} w={10} h={14} rx={2} fill={PANTS} seed={1680} />
    </g>
  );
}
